import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import without from 'lodash/without';
import union from 'lodash/union';

import { SearchContext } from './search-context';
import { parseSearchResults, getSearchResults } from '../search-filter-reducer';
import { withError } from '../../shared/components/error-boundary';

/**
 * Helper function to try to use live search from the browser win available.
 * Sometimes the history lags and you get the previous pages search values
 * @param {*} history
 */
const getSearchQuery = history => {
    if (typeof document !== 'undefined') {
        return document.location.search;
    }
    return history.location.search;
};

/**
Context provider for search facet filte selection
@examples
```jsx
<SearchContainer>
    <FilterPage />
<SearchContainer/>
```
@component SearchContainer
@import SearchContainer
@example ../docs/SearchContainer.md
@returns {ReactElement} The rendered component
*/
export class SearchContainer extends React.PureComponent {
    static whyDidYouRender = true;
    static propTypes = {
        /**
         * Function that will be called when facet selections change
         */
        searchFunc: PropTypes.func.isRequired,
        /**
         * Unique key for redux persitence for results. Used for SSR
         */
        searchKey: PropTypes.string.isRequired,
    };

    static defaultProps = {
        customContext: {},
        search: {},
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.disableSearch !== prevState.disableSearch) {
            return {
                disableSearch: nextProps.disableSearch,
            };
        }

        if (nextProps.searchKey !== prevState.searchKey) {
            return {
                customContext: nextProps.customContext,
                searchKey: nextProps.searchKey,
            };
        }
        return null;
    }

    constructor(props) {
        super(props);
        const { search, enablePagination, setFilterCount, disableSearch } = props;
        const {
            query,
            results,
            currentPage,
            hits,
            sort,
            aggregations,
            pageSize,
            totalPages,
            customAggregations,
            dynamicSEO,
        } = search;
        const filterCount = setFilterCount ? setFilterCount(query) : Object.keys(query).length;
        // State also contains the updater function so it will
        // be passed down into the context provider
        this.state = {
            updateQuery: this.updateQuery,
            setQuery: this.setQuery,
            setSort: this.setSort,
            setPage: this.setPage,
            updateFilterUrl: this.updateFilterUrl,
            getFilterUrl: this.getFilterUrl,
            onQueryComplete: this.onQueryComplete,
            query,
            results,
            currentPage,
            pageSize,
            totalPages,
            hits,
            sort,
            aggregations,
            paginationEnabled: enablePagination,
            disableSearch,
            searchInProgress: false,
            customAggregations,
            filterCount,
            dynamicSEO,
        };
    }

    componentDidMount() {
        // if no results on initial load, call search function
        const { results, query, currentPage, sort, aggregations, pageSize } = this.state;
        const { urlSerialize, urlParser, match } = this.props;

        if (results.length === 0) {
            const initQuery = urlSerialize && urlParser ? urlParser(match) : query;
            this.doSearch(results, initQuery, currentPage, sort, aggregations, pageSize);
        }
    }

    componentDidUpdate(prevProps) {
        const { searchKey: prevSearchKey, disableSearch: prevDisableSearch } = prevProps,
            { searchKey, search, disableSearch, match, urlSerialize, urlParser } = this.props;

        /** When search is enabled or searchKey changes, search */
        if (
            (searchKey && prevSearchKey && searchKey !== prevSearchKey && !disableSearch) ||
            (prevDisableSearch && !disableSearch)
        ) {
            const { query, results, currentPage, sort, aggregations, pageSize } = search;
            const initQuery = urlSerialize && urlParser ? urlParser(match) : query;

            this.doSearch(results, initQuery, currentPage, sort, aggregations, pageSize, true);
        }
    }

    resetSort() {}

    doSearch = (results, query, currentPage, sort, aggregations, pageSize, resetResults = false) => {
        const startPage = pageSize * (currentPage - 1);
        const { searchKey, searchFunc, resultMapper, disableSearch, urlSerialize, customContext, setFilterCount } =
            this.props;

        if (disableSearch) {
            return Promise.resolve('Search disabled');
        }
        const filterCount = setFilterCount ? setFilterCount(query) : Object.keys(query).length;

        this.setState({
            searchInProgress: true,
        });

        return searchFunc(query, aggregations, sort, startPage, pageSize, searchKey, currentPage)
            .then(({ data, customAggregations, dynamicSEO }) => {
                let seralizedQuery = this.getFilterUrl(query, customAggregations);
                let newState = parseSearchResults(
                    this.state,
                    data,
                    currentPage,
                    sort,
                    query,
                    resetResults,
                    resultMapper,
                    customAggregations,
                    filterCount,
                    dynamicSEO,
                );

                newState.searchInProgress = false;
                newState.seralizedQuery = seralizedQuery;
                this.setState(newState, () => {
                    //if url serialization is enabled
                    if (urlSerialize && seralizedQuery) {
                        this.setFilterUrl(seralizedQuery);
                    }
                });
            })
            .catch(e => {
                //do something with this error
                console.log('Error fetching results', e);
                this.setState({
                    searchInProgress: false,
                });
            });
    };

    /**
     * Merge existing query with new values
     * @param  {Object} query Query object with values to merged into exisiting
     * @return {[type]}       [description]
     */
    updateQuery = (action, value) => {
        //update state by performing search with new query value
        const { results, query, currentPage, sort, aggregations, pageSize } = this.state;

        //loop through fields and add or remove from query
        let newQuery = Object.keys(value).reduce(
            (result, field) => {
                let fieldValue = value[field];
                fieldValue = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
                if (action === 'remove') {
                    result[field] = without(result[field], ...fieldValue);
                } else if (action === 'replace') {
                    result[field] = fieldValue;
                } else {
                    result[field] = union(result[field], fieldValue);
                }

                if (result[field] && result[field].length === 0) {
                    delete result[field];
                }

                return result;
            },
            { ...query },
        );

        //reset to page one when query is changes
        this.doSearch(results, newQuery, 1, sort, aggregations, pageSize, true);
    };

    /**
     * Sets query to exacty value provided
     * @param  {Object} query Query object to overrwite exisiting query
     * @return {[type]}       [description]
     */
    setQuery = query => {
        const { results, sort, aggregations, pageSize } = this.state;
        this.doSearch(results, query, 1, sort, aggregations, pageSize, true);
    };

    setSort = (newSort, offlineSort) => {
        //if offline sort function provided, run results through
        //the provided sort function and set state
        if (offlineSort) {
            const { results } = this.state;
            const { searchKey, pageSize } = this.props;
            this.setState({
                results: offlineSort(searchKey, results, newSort, pageSize),
                sort: newSort,
            });
        } else {
            // else hit remote API for sorting.
            const {
                results,
                query,
                currentPage,
                sort: oldSort,
                aggregations,
                pageSize,
                paginationEnabled,
            } = this.state;
            const { field, order, fields } = newSort;

            // if changing sort order flush results since pages will change
            // and if pagination is not enabled set page back to zero
            if (field !== oldSort.field || order !== oldSort.order || fields !== oldSort.fields) {
                const sortObj = { field, order };

                this.doSearch(
                    results,
                    query,
                    paginationEnabled ? currentPage : 1,
                    sortObj,
                    aggregations,
                    pageSize,
                    true,
                );
            }
        }
    };

    /**
     * Takes query and returns the serialized version
     * Could alway some prop to control this to make component more reusuable
     * @return {[type]} [description]
     */
    getFilterUrl = (query, customAggregations) => {
        const { match, urlFormatter, customContext } = this.props;
        let pathname;
        if (urlFormatter) {
            pathname = urlFormatter(match, { query }, customAggregations, customContext);
        }
        return pathname;
    };

    /**
     * Sets the pathname for filter, used for when the search completes
     * Could alway some prop to control this to make component more reusuable
     * @return {[type]} [description]
     */
    setFilterUrl = pathname => {
        const { history } = this.props;
        const { pathname: currentPathname } = history.location;
        if (pathname) {
            let location = { pathname, search: getSearchQuery(history) };
            history.push(location);
        }
    };

    /**
     * Update the url with a given serialized url string and searchs
     */
    updateFilterUrl = filterString => {
        const { urlSerialize, urlParser, match } = this.props;
        if (!urlSerialize || !urlParser || !filterString) {
            return null;
        }
        const matchClone = JSON.parse(JSON.stringify(match));
        matchClone.params.filters = filterString;
        this.setQuery(urlParser(matchClone));
    };

    setPage = pageNum => {
        const { results = [], query, sort, aggregations, pageSize } = this.state;
        // if we dont already have the page, fetch it
        if (pageNum > results.length / pageSize) {
            this.doSearch(results, query, pageNum, sort, aggregations, pageSize);
        } else {
            this.setState({
                currentPage: page,
            });
        }
    };

    render() {
        return <SearchContext.Provider value={this.state}>{this.props.children}</SearchContext.Provider>;
    }
}

const mapStateToPropsFactory = (initialState, ownProps) => {
    // a closure for ownProps is created
    // this factory is not invoked everytime the component
    // changes it's props
    return state => {
        return {
            search: getSearchResults(state, ownProps.searchKey, ownProps.resultMapper, ownProps.search),
        };
    };
};

export default connect(mapStateToPropsFactory, null)(withRouter(withError(SearchContainer)));
